import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconButton, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { DraggableSyntheticListeners } from "@dnd-kit/core";
import { faExpand, faCompress, faArrowUpRightFromSquare, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { LiveIndicator } from "../LiveIndicator/LiveIndicator";
import { useOnScreen } from "../Hooks/useOnScreen";
import { DashboardCardContext, LiveReport } from "../Contexts/DashboardCardContext";
import { useLiveUpdates } from "../Hooks/useLiveUpdates";
import { RootState } from "../../state/store";
import { AreaKey } from "../../utilities/helpers/pageVocabulary.generated";
import { hasArea } from "../../utilities/helpers/roleUtilities";
import { CARD_HEIGHT_CLASS, CardHeight } from "./dashboardLayout";

type DashboardCardProps = {
  title?: string;
  linkTo?: string;
  linkTooltip?: string;
  linkArea?: AreaKey;
  children: React.ReactNode;
  height: CardHeight;
  onHide?: () => void;
  editing?: boolean;
  dragListeners?: DraggableSyntheticListeners;
  dragHandle?: React.ReactNode;
  editControls?: React.ReactNode;
};

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  linkTo,
  linkTooltip,
  linkArea,
  children,
  height,
  onHide,
  editing = false,
  dragListeners,
  dragHandle,
  editControls,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isLive = useLiveUpdates();
  const [liveReport, setLiveReport] = useState<LiveReport | null>(null);
  const { user } = useSelector((s: RootState) => s.authReducer);
  const slotRef = useRef<HTMLDivElement>(null);
  const onScreenInGrid = useOnScreen(slotRef);
  const [host] = useState(() => {
    const element = document.createElement("div");
    element.className = "contents";
    return element;
  });

  useLayoutEffect(() => () => host.remove(), [host]);

  useLayoutEffect(() => {
    const focused = host.contains(document.activeElement) ? document.activeElement : null;
    (isFullscreen ? document.body : slotRef.current)?.appendChild(host);
    if (focused instanceof HTMLElement) focused.focus();
  }, [host, isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return;

    const leaveOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) setIsFullscreen(false);
    };
    window.addEventListener("keydown", leaveOnEscape);
    return () => window.removeEventListener("keydown", leaveOnEscape);
  }, [isFullscreen]);

  const onScreen = isFullscreen || onScreenInGrid;

  const linkEnabled = useMemo(() => {
    if (!linkTo) return false;
    if (!linkArea) return true;
    return hasArea(user, linkArea);
  }, [linkTo, linkArea, user]);

  const cardContext = useMemo(
    () => ({ isFullScreen: isFullscreen, onScreen, editing, reportLive: setLiveReport }),
    [isFullscreen, onScreen, editing]
  );

  return (
    <div
      ref={slotRef}
      className={clsx("w-full", isFullscreen && CARD_HEIGHT_CLASS[height])}
    >
      {isFullscreen && createPortal(
        <div
          data-testid="fullscreen-backdrop"
          className="fixed inset-0 bg-black/70 z-40 animate-fadeIn"
          onClick={() => setIsFullscreen(false)}
        />,
        document.body
      )}
      {createPortal(
        <div
          className={clsx(
            "w-full flex flex-col border border-theme-border shadow-[0_0_4px_var(--theme-shadow)] bg-theme-surface-2 md:rounded-lg transition-shadow ease-in-out",
            isFullscreen
              ? "fixed top-1/2 md:top-1/2 left-1/2 w-full h-screen md:w-[95vw] md:h-[90vh] z-50 -translate-x-1/2 -translate-y-1/2 overflow-auto"
              : clsx("relative w-full overflow-hidden hover:shadow-[0_0_12px_1px_var(--theme-shadow)] [content-visibility:auto]", CARD_HEIGHT_CLASS[height])
          )}
        >
          <div
            {...dragListeners}
            className={clsx(
              isFullscreen ? "pt-10 pb-6" : "py-4",
              "@container px-3 grid grid-cols-[1fr_minmax(0,auto)_1fr] gap-2 items-center border-b border-theme-border bg-theme-surface",
              editing && "cursor-grab"
            )}
          >
            <div className="col-start-1 justify-self-start">{dragHandle}</div>

            {title && (
              <div className="col-start-2 flex items-center min-w-0">
                {linkTo && linkEnabled && !editing ? (
                  <Link
                    to={linkTo}
                    title={linkTooltip ?? `Open ${title}`}
                    className={`group inline-flex items-start min-w-0 font-semibold text-theme-text hover:text-theme-text-muted transition-colors ${isFullscreen ? "text-3xl md:text-4xl" : "text-2xl @max-md:text-xl"}`}
                  >
                    <span className="truncate">{title}</span>
                    <FontAwesomeIcon
                      icon={faArrowUpRightFromSquare}
                      className={`transition-opacity mt-1 ${isFullscreen ? "text-sm md:text-md" : "text-[8px]"}`}
                    />
                  </Link>
                ) : (
                  <h3 className={`${isFullscreen ? "text-3xl md:text-4xl" : "text-2xl @max-md:text-xl"} font-semibold text-theme-text truncate`}>
                    {title}
                  </h3>
                )}
                <LiveIndicator
                  active={isLive && liveReport !== null}
                  label={isLive && liveReport?.current ? "LIVE" : undefined}
                  tooltipText={liveReport?.tooltipText}
                  size={isFullscreen ? 15 : 12}
                  className="ml-2 mb-2 d-inline-block"
                />
              </div>
            )}

            <div className={`${isFullscreen ? "self-start -mt-8" : ""} col-start-3 justify-self-end flex items-center gap-1`}>
              {onHide && !isFullscreen && (
                <Tooltip title="Hide card" placement="top">
                  <IconButton
                    aria-label={`Hide ${title}`}
                    onClick={onHide}
                    className="!bg-theme-surface hover:!bg-theme-surface-2 !text-theme-text-muted hover:!text-theme-text transition-colors !text-lg"
                  >
                    <FontAwesomeIcon icon={faEyeSlash} />
                  </IconButton>
                </Tooltip>
              )}
              {!editing && (
                <IconButton
                  aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  onClick={() => setIsFullscreen((prev) => !prev)}
                  className={`!bg-theme-surface hover:!bg-theme-surface-2 !text-theme-text transition-colors ${isFullscreen ? "!text-3xl" : "!text-lg"}`}
                >
                  <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                </IconButton>
              )}
            </div>
          </div>

          <div className="flex-grow min-h-0 overflow-hidden">
            <div className="@container-size/card h-full w-full flex justify-center">
              <DashboardCardContext value={cardContext}>{children}</DashboardCardContext>
            </div>
          </div>

          {editControls}
        </div>,
        host
      )}
    </div>
  );
};
